import { getLocalStorageItem } from '../../../logger/src/LocalStorage';

/**
 * Webex Token enum key under which the signed-in user's org id is persisted
 * by `MsTeamsAuthenticationService`. Defined here as a string literal so this
 * utility stays independent of the `ms-teams-sdk` module (which is not part
 * of the widgets package).
 *
 * Keep this value in sync with `WebexTokenEnum.ORG_ID` in
 * `src/ms-teams-sdk/service/MsTeamsAuthenticationService.ts`.
 */
const WEBEX_ORG_ID_STORAGE_KEY = 'webex_orgId';

/**
 * Allow-list of Webex organization ids whose users must NOT be shown the
 * Calling Dock first-time-user-experience (FTUX) intro modal nor the
 * "Turn off the Calling dock?" confirmation modal.
 *
 * Background:
 *   The Calling Dock UX surfaces two popups by default:
 *     1. FTUX intro (`CallingWidgetModal`) on first sign-in for users whose
 *        org has `calling.callingdock.enabled` provisioned.
 *     2. Off-confirmation (`CallingWidgetOffModal`) the first time a user
 *        toggles the dock off.
 *
 *   Some customers have asked that their users be opted-out of both popups
 *   entirely so the toggle behaves silently (on/off with no explainer
 *   modals). For those orgs we still show the toggle in the user-profile
 *   menu — only the popups are suppressed.
 *
 * Org ids are case-insensitive UUIDs and are compared after trimming and
 * lower-casing the value read from localStorage to avoid any whitespace or
 * casing mismatches.
 *
 * Add new orgs here only after the customer has confirmed they want to
 * skip the explainer popups for their entire user base.
 */
export const CALLING_DOCK_SUPPRESSED_ORG_IDS: ReadonlyArray<string> = [
  // UHS (Universal Health Services) — customer request to suppress both the
  // Calling Dock FTUX intro modal and the off-confirmation modal for every
  // user in the org.
  'b1109f3e-8a85-4727-9d66-bcfa884de3e9',
  '88d61729-9c86-4bbf-87d1-3df2cf37dd2c',
];

const CALLING_DOCK_SUPPRESSED_ORG_ID_SET: ReadonlySet<string> = new Set(
  CALLING_DOCK_SUPPRESSED_ORG_IDS.map((id) => id.trim().toLowerCase()),
);

/**
 * Returns true when the supplied org id is in the suppression allow-list,
 * i.e. the Calling Dock FTUX and off-confirmation modals must be hidden
 * for users in that org.
 *
 * @param orgId - Webex organization id (case-insensitive). May be `null`,
 *   `undefined`, or an empty string for unauthenticated states; those cases
 *   return `false` so the default behaviour (popups visible) is preserved.
 */
export function isCallingDockSuppressedOrg(
  orgId: string | null | undefined,
): boolean {
  if (!orgId || typeof orgId !== 'string') {
    return false;
  }

  return CALLING_DOCK_SUPPRESSED_ORG_ID_SET.has(orgId.trim().toLowerCase());
}

/**
 * Convenience wrapper that reads the signed-in user's org id from
 * localStorage and reports whether the Calling Dock popups must be
 * suppressed for them. Returns `false` when no org id is present yet
 * (e.g. before sign-in completes) so the default behaviour is preserved.
 */
export function isCallingDockSuppressedForCurrentUser(): boolean {
  const orgId = getLocalStorageItem<string>(WEBEX_ORG_ID_STORAGE_KEY);

  return isCallingDockSuppressedOrg(orgId);
}
